import React, { RefObject } from 'react';
import { differenceInYears } from 'date-fns';
import { Modals } from '../contexts/ModalsContext';
import { PatientProps } from '../contexts/PatientContext';
import { CoverageProps, Rule } from '../models/Product';
import { ScheduleItemProps, typeLocationSchedule } from '../models/Schedule';
import { saveSalesForceEAV } from '../modules/slots';
import { useAttendanceUnitsWithDates } from './useAttendanceUnitsWithDates';
import { ScheduleChannelsMapped } from '../utils/enums/channels';
import { UnitsAndResources } from './useGenericModal';
import { Unit } from '../components/ServiceItem';
import { useSchedulingMount } from './useSchedulingMount';
import { UnitDatesAvailable } from '../contexts/ScheduleContext';

export type CoverageUnitSchedule = {
  coverage?: CoverageProps;
  unit?: Unit;
  address?: Location;
};

type useSelectCoverageProps = {
  handleModalProps: (props: { condition?: string | undefined; visible: boolean }) => void;
  openModal: (modal: keyof typeof Modals) => void;
  scheduleSelected: ScheduleItemProps | undefined;
  productRules: Rule[];
  changeSchedule: (schedule: ScheduleItemProps) => ScheduleItemProps;
  addUnitDatesAvailable: (unitTimes: UnitDatesAvailable) => void;
  submitButtonRef?: RefObject<HTMLButtonElement>;
  setModalServiceUnavailable?: (value: boolean) => void;
};

export const useSelectCoverage = ({
  handleModalProps,
  openModal,
  scheduleSelected,
  productRules,
  changeSchedule,
  addUnitDatesAvailable,
  submitButtonRef,
  setModalServiceUnavailable
}: useSelectCoverageProps) => {
  const [specificUnitsResources, setSpecificUnitsResources] = React.useState<UnitsAndResources>();
  const [coverageUnit, setCoverageUnit] = React.useState<CoverageUnitSchedule | undefined>();
  const [selectedCoverage, setSelectedCoverage] = React.useState<CoverageProps>();
  const [selectedUnit, setSelectedUnit] = React.useState<Unit>();

  const useScheduling = useSchedulingMount({ changeSchedule, selectedSchedule: scheduleSelected });
  const { loading, openToScheduleUnits, closedToScheduleUnits, unitDatesAvailable } = useAttendanceUnitsWithDates(
    selectedCoverage,
    scheduleSelected,
    specificUnitsResources,
    handleModalProps,
    addUnitDatesAvailable,
    productRules
  );

  React.useEffect(() => {
    let hasSlots = false;

    unitDatesAvailable.map(item => {
      if (item.dates.length) {
        hasSlots = !!item.dates.length;
      }
    });

    if (openToScheduleUnits.length > 0 && hasSlots) {
      const hasOnlyOneUnitToShow = openToScheduleUnits.length === 1;
      if (hasOnlyOneUnitToShow) setUnitToSchedule(openToScheduleUnits[0]);

      if (selectedCoverage?.care_type === 'TELEMEDICINE' && hasOnlyOneUnitToShow) {
        submitButtonRef?.current?.click();
      }
    }
  }, [openToScheduleUnits, selectedUnit]);

  const handleSelectedCoverage = async (props: {
    coverage: CoverageProps;
    patientSelected: PatientProps | undefined;
  }) => {
    const careType = props.coverage.care_type;
    resetCoverageSelection();

    if (props.coverage.units?.length === 0 && props.coverage.channel !== ScheduleChannelsMapped.TELEORIENTATION)
      openModal(Modals.PRODUCT_NOT_COVERED);

    if (props.coverage.appointment_status === 'WHATSAPP_DIRECTION' || careType === typeLocationSchedule.EINSTEIN_TO_YOU)
      setModalServiceUnavailable?.(true);

    if (props.coverage.appointment_status !== 'WHATSAPP_DIRECTION') {
      if (careType === typeLocationSchedule.DRIVE_THRU) {
        if (props.patientSelected) {
          const age = differenceInYears(new Date(), new Date(props.patientSelected.birthDate));
          if (age < 9) {
            openModal(Modals.NINE_YEARS_DRIVE_THRU);
            return;
          }
        }
      }
      // else if (careType === typeLocationSchedule.EINSTEIN_TO_YOU && props.patientSelected) {
      //   await saveSalesForceEAV({
      //     patient: {
      //       firstName: props.patientSelected.name.slice(0, 1),
      //       lastName: props.patientSelected.name.slice(1),
      //       document: props.patientSelected.document,
      //       phoneNumber: props.patientSelected.phone,
      //       email: props.patientSelected.email,
      //       birthDate: props.patientSelected.birthDate,
      //       documentType: props.patientSelected.documentType,
      //       sex: props.patientSelected.gender
      //     }
      //   });
      // }
      setSelectedCoverage(props.coverage);
      setCoverageUnit({
        ...coverageUnit,
        coverage: props.coverage,
        unit: undefined
      });
    }
    useScheduling.addCareType(props.coverage.care_type);
  };

  const setUnitToSchedule = async (unit: Unit) => {
    setSelectedUnit(unit);
    useScheduling.addUnit({
      unit,
      localId: unit?.local_id || unit?.locations?.[0]?.name || ''
    });
    useScheduling.updateSchedule();
  };

  const resetCoverageSelection = () => {
    setSelectedUnit(undefined);
  };

  const resetCoverageSelected = () => setSelectedCoverage(undefined);

  return {
    coverageUnit,
    selectedCoverage,
    setSpecificUnitsResources,
    loading,
    openToScheduleUnits,
    closedToScheduleUnits,
    unitDatesAvailable,
    handleSelectedCoverage,
    setUnitToSchedule,
    resetCoverageSelection,
    selectedUnit,
    resetCoverageSelected
  };
};
